<template>
    <!-- 3D Secure Modal Overlay -->
    <div v-if="show3DS" class="fixed inset-0 z-[9999] flex items-center justify-center p-4">
       <!-- Backdrop -->
       <div @click="show3DS = false" class="absolute inset-0 bg-slate-900/60 backdrop-blur-sm transition-opacity"></div>
       
       <!-- Modal Content -->
       <div class="relative w-full max-w-lg overflow-hidden rounded-3xl bg-white shadow-2xl transition-all">
          <div class="flex items-center justify-between border-b border-slate-100 px-6 py-4">
             <h3 class="text-lg font-bold text-slate-900">Secure Payment</h3>
             <button @click="show3DS = false" class="rounded-full p-2 hover:bg-slate-100 transition-colors">
                <font-awesome-icon icon="fa-solid fa-xmark" class="text-slate-500" />
             </button>
          </div>
          <div id="3DSUI" class="max-h-[80vh] w-full overflow-y-auto bg-white p-6 shadow-inner">
             <!-- Bank Content Injected Here -->
          </div>
       </div>
    </div>

    <div class="grid grid-cols-12 md:gap-10">
      <div class="col-span-12 md:col-span-3 flex flex-col h-fit order-2 md:order-1 mt-12 md:mt-0">
         <BookingSummary :key="summaryKey" :currentStep="currentStep" :couponDiscount="appliedDiscount" :newTotal="newTotal" />
      </div>

      <!-- PAYMENT PROCESSING OVERLAY -->
      <div v-if="isLoading" class="fixed inset-0 z-[10000] flex flex-col items-center justify-center bg-slate-900/40 backdrop-blur-md">
         <div class="bg-white p-8 rounded-3xl shadow-2xl flex flex-col items-center gap-4 max-w-sm text-center border border-slate-100">
            <div class="w-16 h-16 border-4 border-theme-active border-t-transparent rounded-full animate-spin"></div>
            <h3 class="text-xl font-bold text-slate-900">Processing Payment</h3>
            <p class="text-slate-600">Securely connecting to your bank. Please do not refresh the page...</p>
         </div>
      </div>

      <div class="col-span-12 md:col-span-9 flex flex-col order-1 md:order-2">
         <!-- CONTACT DETAILS BLOCK -->
         <div class="flex flex-col border border-theme-gray-7 rounded-2xl overflow-hidden">
            <div class="bg-theme-gray-7 px-5 py-3.5 text-theme-gray-2 text-size-2 uppercase font-bold">
               <span v-if="!isLogged">Guest </span>Contact details
            </div>
            <div class="flex flex-col">
               <div class="grid grid-cols-2 md:divide-x divide-y md:divide-y-0 divide-theme-gray-7">
                  <div class="col-span-2 md:col-span-1 flex flex-col gap-2 items-start px-4 py-5">
                     <div :class="!errors.firstName ? 'text-theme-gray-2' : 'text-theme-error'" class="uppercase text-small flex items-center gap-1">
                        <span>First name *</span>
                        <span v-if="errors.firstName" class="text-theme-error">(required)</span>
                     </div>
                     <input v-model="contactDetails.firstName" type="text" class="outline-none w-full h-30 font-bold text-size-6 border-none " placeholder="Enter first name">
                  </div>
                  <div class="col-span-2 md:col-span-1">
                     <div class="col-span-1 flex flex-col gap-2 items-start px-4 py-5">
                        <div :class="!errors.lastName ? 'text-theme-gray-2' : 'text-theme-error'" class="uppercase text-small flex items-center gap-1">
                           <span>Last name *</span>
                           <span v-if="errors.lastName" class="text-theme-error">(required)</span>
                        </div>
                        <input v-model="contactDetails.lastName" type="text" class="outline-none w-full h-30 font-bold text-size-6 border-none " placeholder="Enter last name">
                     </div>
                  </div>
               </div>
               <div class="grid grid-cols-2 md:divide-x divide-y md:divide-y-0 divide-theme-gray-7 border-t border-theme-gray-7">
                  <div class="col-span-2 md:col-span-1 flex flex-col gap-2 items-start px-4 py-5">
                     <div :class="!errors.email ? 'text-theme-gray-2' : 'text-theme-error'" class="uppercase text-small flex items-center gap-1">
                        <span>Email *</span>
                        <span v-if="errors.email" class="text-theme-error">({{ errors.email }})</span>
                     </div>
                     <input v-model="contactDetails.email" type="text" class="outline-none w-full h-30 font-bold text-size-6 border-none " placeholder="Enter email address">
                  </div>
                  <div class="col-span-2 md:col-span-1">
                     <div class="col-span-1 flex flex-col gap-2 items-start px-4 py-5 relative">
                        <div :class="!errors.phoneNumber ? 'text-theme-gray-2' : 'text-theme-error'" class="uppercase text-small flex items-center gap-1">
                           <span>Phone number *</span>
                           <span v-if="errors.phoneNumber" class="text-theme-error">({{ errors.phoneNumber }})</span>
                        </div>
                        <CountrySearchablePhoneField @phoneNumber="handlePhoneChange" />
                     </div>
                  </div>
               </div>
               <div class="grid grid-cols-2 md:divide-x divide-y md:divide-y-0 divide-theme-gray-7 border-t border-theme-gray-7">
                  <div class="col-span-2 md:col-span-1 flex flex-col gap-2 items-start px-4 py-5">
                     <div :class="!errors.passengers ? 'text-theme-gray-2' : 'text-theme-error'" class="uppercase text-small flex items-center gap-1">
                        <span>No. of passengers *</span>
                        <span v-if="errors.passengers" class="text-theme-error">(required)</span>
                     </div>
                     <input v-model="contactDetails.passengers" type="number" class="outline-none w-full h-30 font-bold text-size-6 border-none placeholder:font-normal placeholder:text-gray-400 placeholder:opacity-60" placeholder="e.g. 2">
                  </div>
                  <div class="col-span-2 md:col-span-1 flex flex-col gap-2 items-start px-4 py-5">
                     <div :class="!errors.bags ? 'text-theme-gray-2' : 'text-theme-error'" class="uppercase text-small flex items-center gap-1">
                        <span>No. of bags *</span>
                        <span v-if="errors.bags" class="text-theme-error">(required)</span>
                     </div>
                     <input v-model="contactDetails.bags" type="number" class="outline-none w-full h-30 font-bold text-size-6 border-none placeholder:font-normal placeholder:text-gray-400 placeholder:opacity-60" placeholder="e.g. 1">
                  </div>
               </div>
               <!-- CAPACITIES WARNING & UPGRADE SUGGESTIONS -->
               <div v-if="showUpgradeSuggestions" class="flex flex-col border-t border-theme-gray-7 bg-amber-50/50 p-5">
                  <div class="flex items-start gap-3">
                     <span class="text-xl leading-none">⚠️</span>
                     <div class="flex-1">
                        <h4 class="text-amber-800 font-bold text-sm uppercase">Capacity Limit Warning</h4>
                        <p class="text-slate-600 text-xs mt-1">
                           The selected <strong class="text-slate-900">{{ car?.name }}</strong> can carry a maximum of <strong>{{ car?.type?.passengers }} passengers</strong> and <strong>{{ car?.type?.bags }} bags</strong>.
                        </p>
                        <p class="text-slate-600 text-xs mt-0.5">
                           Since you entered <strong>{{ contactDetails.passengers }} passengers</strong> and <strong>{{ contactDetails.bags }} bags</strong>, please select a vehicle with suitable capacity below:
                        </p>
                     </div>
                  </div>

                  <!-- Suggested Upgrades list -->
                  <div v-if="suggestedCars.length > 0" class="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                     <div v-for="sCar in suggestedCars" :key="sCar.id" class="flex flex-col border border-amber-200 rounded-2xl p-4 bg-white shadow-sm hover:border-amber-400 hover:shadow transition-all">
                        <img :src="getCarImage(sCar)" class="h-20 object-contain mx-auto mb-2" alt="vehicle" />
                        <h5 class="text-slate-900 font-bold text-xs truncate text-center">{{ sCar.name }}</h5>
                        <p class="text-slate-500 text-[10px] text-center uppercase tracking-wider mb-2">{{ sCar.type?.name }}</p>
                        
                        <!-- Capacities -->
                        <div class="flex justify-center gap-3 text-xs text-slate-600 mb-3">
                           <span class="inline-flex items-center gap-1">👤 {{ sCar.type?.passengers }}</span>
                           <span class="inline-flex items-center gap-1">🧳 {{ sCar.type?.bags }}</span>
                        </div>

                        <!-- Price -->
                        <div class="text-center font-bold text-slate-900 text-sm mb-3">
                           {{ sCar.price > 0 ? `${(userSearch?.currency || '$')} ${sCar.price.toFixed(2)}` : 'On Request' }}
                        </div>

                        <!-- Action -->
                        <button 
                           @click="switchCar(sCar)" 
                           :disabled="isSwitchingCar"
                           class="w-full text-center bg-amber-500 hover:bg-amber-600 disabled:bg-slate-300 text-[#0a0d0f] font-bold text-xs py-2 px-3 rounded-full transition-colors"
                        >
                           <span v-if="isSwitchingCar && switchingCarId === sCar.id">
                              Switching...
                           </span>
                           <span v-else>
                              Switch to this car
                           </span>
                        </button>
                     </div>
                  </div>
                  <div v-else class="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-2xl text-center text-xs text-amber-800">
                     No pre-priced vehicles are currently available with this capacity in our system. 
                     <strong>You can proceed with this booking as a "Request a Quote" booking request</strong>. 
                     Our operations team will manually assign a suitable vehicle and quote you the price.
                  </div>
               </div>
               <div class="flex flex-col border-t border-theme-gray-7">
                  <div class="col-span-1 flex flex-col gap-2 items-start px-4 py-5">
                     <div class="uppercase text-theme-gray-2 text-small">Comments</div>
                     <textarea v-model="contactDetails.comments" autocomplete="off" name="booking_comments" class="outline-none w-full h-30 font-bold text-size-6 border-none h-[60px] placeholder:font-normal placeholder:text-gray-400 placeholder:opacity-60" placeholder="Extra requirements or special notes (e.g. child seat, wheelchair etc.)"></textarea>
                  </div>
               </div>
               <!-- Only show create account for non-logged in users -->
               <div v-if="!isLogged" class="flex items-center gap-2 px-4 py-3 bg-theme-gray-7/30 border-t border-theme-gray-7">
                  <input v-model="contactDetails.createAccount" type="checkbox" id="create-account" class="w-4 h-4 rounded border-gray-300 text-theme-active focus:ring-theme-active cursor-pointer">
                  <label for="create-account" class="text-[10px] md:text-small font-bold text-theme-gray-2 uppercase cursor-pointer select-none">
                     Save my details and create an account to track my booking
                  </label>
               </div>
            </div>
         </div>

         <!-- PAYMENT OPTIONS TOGGLE (only if B2B wallet is enabled) -->
         <div v-if="isLogged && authUser?.user?.type === 'agent' && agencyWallet.allow_wallet_booking && !contactDetails.needConfirmation" class="flex gap-4 mt-6">
            <button 
               @click="selectedPaymentMethod = 'card'"
               :class="selectedPaymentMethod === 'card' ? 'border-theme-active bg-theme-active/5 text-theme-active font-black' : 'border-theme-gray-7 bg-white text-slate-500 font-bold'"
               class="flex-1 py-4 px-6 border-2 rounded-2xl flex items-center justify-center gap-3 transition-all duration-300 cursor-pointer"
            >
               <font-awesome-icon icon="fa-solid fa-credit-card" />
               <span class="uppercase text-size-2">Pay by Credit Card</span>
            </button>
            <button 
               @click="selectedPaymentMethod = 'wallet'"
               :class="selectedPaymentMethod === 'wallet' ? 'border-theme-active bg-theme-active/5 text-theme-active font-black' : 'border-theme-gray-7 bg-white text-slate-500 font-bold'"
               class="flex-1 py-4 px-6 border-2 rounded-2xl flex items-center justify-center gap-3 transition-all duration-300 cursor-pointer"
            >
               <font-awesome-icon icon="fa-solid fa-wallet" />
               <span class="uppercase text-size-2">Pay by Agency Wallet</span>
            </button>
         </div>

         <!-- PAYMENT DETAILS BLOCK -->
         <div v-if="!contactDetails.needConfirmation && selectedPaymentMethod === 'card'" class="flex flex-col mt-6 border border-theme-gray-7 rounded-2xl overflow-hidden">
            <div class="bg-theme-gray-7 px-5 py-3.5 text-theme-gray-2 text-size-2 uppercase font-bold">Payment details</div>
            <div class="flex flex-col">
               <div class="grid grid-cols-2 md:divide-x divide-y md:divide-y-0 divide-theme-gray-7">
                  <div class="col-span-2 md:col-span-1 flex flex-col gap-2 items-start px-4 py-5">
                     <div :class="!errors.cardName ? 'text-theme-gray-2' : 'text-theme-error'" class="uppercase text-small flex items-center gap-1">
                        <span>Card holder name *</span>
                        <span v-if="errors.cardName" class="text-theme-error">(required)</span>
                     </div>
                     <input v-model="paymentDetails.card.name" type="text" class="outline-none w-full h-30 font-bold text-size-6 border-none " placeholder="Name as on card">
                  </div>
                  <div class="col-span-2 md:col-span-1">
                     <div class="col-span-1 flex flex-col gap-2 items-start px-4 py-5">
                        <div :class="!errors.cardNumber ? 'text-theme-gray-2' : 'text-theme-error'" class="uppercase text-small flex items-center gap-1">
                           <span>Card number *</span>
                           <span v-if="errors.cardNumber" class="text-theme-error">({{ errors.cardNumber }})</span>
                        </div>
                        <input v-model="paymentDetails.card.number" type="number" class="outline-none w-full h-30 font-bold text-size-6 border-none " placeholder="0000 0000 0000 0000">
                     </div>
                  </div>
               </div>
               <div class="grid grid-cols-2 md:divide-y divide-y md:divide-y-0 divide-theme-gray-7 border-t border-theme-gray-7">
                  <div class="col-span-2 md:col-span-1 flex flex-col gap-2 items-start px-4 py-5 border-r border-theme-gray-7">
                     <div :class="!errors.cardCVC ? 'text-theme-gray-2' : 'text-theme-error'" class="uppercase text-small flex items-center gap-1">
                        <span>CVC *</span>
                        <span v-if="errors.cardCVC" class="text-theme-error">(required)</span>
                     </div>
                     <input v-model="paymentDetails.card.cvc" type="number" class="outline-none w-full h-30 font-bold text-size-6 border-none " placeholder="123">
                  </div>
                  <div class="col-span-2 md:col-span-1 flex flex-col gap-2 items-start px-4 py-5">
                     <div :class="!errors.cardExpiration ? 'text-theme-gray-2' : 'text-theme-error'" class="uppercase text-small flex items-center gap-1">
                        <span>Expiration *</span>
                        <span v-if="errors.cardExpiration" class="text-theme-error">(required)</span>
                     </div>
                     <div class="flex gap-4 w-full pt-1">
                        <div class="flex flex-col flex-1 gap-1">
                           <label class="text-[10px] text-theme-gray-2 uppercase">Month</label>
                           <select v-model="expiryMonth" @change="updateExpiry" class="outline-none bg-theme-gray-7 rounded px-2 py-2 font-bold text-size-5 cursor-pointer appearance-none text-center">
                              <option value="" disabled>MM</option>
                              <option v-for="m in 12" :key="m" :value="m.toString().padStart(2, '0')">
                                 {{ m.toString().padStart(2, '0') }}
                              </option>
                           </select>
                        </div>
                        <div class="flex flex-col flex-1 gap-1">
                           <label class="text-[10px] text-theme-gray-2 uppercase">Year</label>
                           <select v-model="expiryYear" @change="updateExpiry" class="outline-none bg-theme-gray-7 rounded px-2 py-2 font-bold text-size-5 cursor-pointer appearance-none text-center">
                              <option value="" disabled>YYYY</option>
                              <option v-for="y in 15" :key="y" :value="(new Date().getFullYear() + y - 1).toString()">
                                 {{ new Date().getFullYear() + y - 1 }}
                              </option>
                           </select>
                        </div>
                     </div>
                  </div>
               </div>
               <div v-if="errors.responseError" class="text-theme-error p-4">
                  {{ errors.responseError }}
               </div>
            </div>
         </div>

         <!-- AGENCY WALLET DETAILS BLOCK -->
         <div v-if="!contactDetails.needConfirmation && selectedPaymentMethod === 'wallet'" class="flex flex-col mt-6 border border-theme-gray-7 rounded-2xl overflow-hidden bg-slate-50">
            <div class="bg-theme-gray-7 px-5 py-3.5 text-theme-gray-2 text-size-2 uppercase font-bold">Agency Wallet Booking</div>
            <div class="p-6 flex flex-col gap-4 text-left">
               <div class="flex justify-between items-center bg-white p-4 rounded-xl border border-slate-100 shadow-sm">
                  <div>
                     <div class="text-[10px] uppercase font-bold text-slate-400">Available Wallet Balance</div>
                     <div class="text-2xl font-black text-slate-800 pt-1">
                        {{ agencyWallet.wallet_currency }} {{ agencyWallet.wallet_balance.toFixed(2) }}
                     </div>
                  </div>
                  <font-awesome-icon icon="fa-solid fa-wallet" size="2xl" class="text-slate-300" />
               </div>
               
               <div v-if="agencyWallet.has_sufficient_balance" class="flex gap-3 items-center bg-emerald-50 border border-emerald-100 p-4 rounded-xl text-emerald-800 text-xs">
                  <font-awesome-icon icon="fa-solid fa-circle-check" size="lg" class="text-emerald-500 shrink-0" />
                  <span>Sufficient balance. This booking will be paid instantly using your agency credit limit.</span>
               </div>
               
               <div v-else class="flex gap-3 items-center bg-rose-50 border border-rose-100 p-4 rounded-xl text-rose-800 text-xs">
                  <font-awesome-icon icon="fa-solid fa-circle-exclamation" size="lg" class="text-rose-500 shrink-0" />
                  <span><strong>Insufficient balance!</strong> Your available wallet credits balance ({{ agencyWallet.wallet_currency }} {{ agencyWallet.wallet_balance.toFixed(2) }}) is less than the booking amount. Please top up or pay by credit card.</span>
               </div>
               
               <div v-if="errors.responseError" class="text-theme-error font-semibold text-sm">
                  {{ errors.responseError }}
               </div>
            </div>
         </div>

         <!-- PROMOTIONAL CODE SECTION -->
         <div v-if="!contactDetails.needConfirmation" class="flex flex-col mt-6 border border-theme-gray-7 rounded-lg overflow-hidden">
             <div class="bg-theme-gray-7 px-5 py-3.5 flex justify-between items-center cursor-pointer" @click="togglePromo">
                 <span class="text-theme-gray-2 text-size-2 uppercase">Apply Promo Code</span>
                 <font-awesome-icon :icon="showPromo ? 'fa-solid fa-chevron-up' : 'fa-solid fa-chevron-down'" class="text-theme-gray-3"/>
             </div>
             <div v-if="showPromo" class="p-5 flex flex-col bg-white">
                 <div class="flex flex-col md:flex-row gap-4">
                    <div class="flex-1 w-full text-left">
                       <input 
                             v-model="promoCode" 
                             type="text" 
                             :disabled="isApplyingPromo"
                             class="outline-none w-full border border-theme-gray-7 rounded-md px-4 py-3 font-bold text-slate-700 placeholder:font-normal uppercase focus:border-theme-active transition-colors" 
                             placeholder="Enter Coupon Code"
                             @input="promoErrorMessage = ''"
                       >
                       <p v-if="promoErrorMessage" class="mt-2 text-theme-error text-sm font-semibold">
                          <font-awesome-icon icon="fa-solid fa-circle-exclamation" class="mr-1" />
                          {{ promoErrorMessage }}
                       </p>
                    </div>
                    <button 
                       @click="applyPromoCode"
                       :disabled="!promoCode || isApplyingPromo"
                       class="bg-[#0B6A1F] h-fit hover:bg-green-800 disabled:bg-slate-300 disabled:cursor-not-allowed text-white uppercase text-sm font-bold tracking-wide px-8 py-3 rounded-md transition-colors whitespace-nowrap"
                    >
                       <span v-if="isApplyingPromo">Applying...</span>
                       <span v-else>Apply Offer</span>
                    </button>
                 </div>
             </div>
         </div>

         <!-- BOTTOM PRICE SUMMARY -->
         <div v-if="!contactDetails.needConfirmation" class="mt-8 p-6 bg-slate-50 rounded-2xl border border-slate-100 flex flex-col gap-3">
             <div class="flex justify-between items-center text-slate-600 font-medium">
                <span class="text-xs uppercase tracking-wider">Subtotal</span>
                <span class="font-bold tracking-tight">{{ (userSearch?.currency || '$') }} {{ (car?.price || 0).toFixed(2) }}</span>
             </div>
             <div v-if="appliedDiscount" class="flex justify-between items-center text-emerald-600 font-medium">
                <span class="text-xs uppercase tracking-wider">Coupon Discount</span>
                <span class="font-bold tracking-tight">- {{ (userSearch?.currency || '$') }} {{ appliedDiscount.toFixed(2) }}</span>
             </div>
             <div class="h-px bg-slate-200/60 my-1"></div>
             <div class="flex justify-between items-center">
                <span class="text-base uppercase font-black text-slate-800 tracking-wide">Total Amount</span>
                <span class="text-2xl font-black text-theme-active">
                   {{ (userSearch?.currency || '$') }} {{ (newTotal || car?.price || 0).toFixed(2) }}
                </span>
             </div>
          </div>

         <div class="flex flex-col md:flex-row items-center gap-4 mt-12">
            <div @click="router.push({name: 'BookingWizard', params:{step: 'choose-vehicle'}})" class="gap-4 uppercase rounded-full px-8 py-4 border-2 border-theme-gray-7 bg-theme-gray-7 hover:bg-white text-theme-gray-2 text-size-2 flex items-center justify-center cursor-pointer transition-all ease-in-out duration-500 w-full md:w-auto order-2 md:order-1">
               <span class="text-theme-gray-2">
                  <font-awesome-icon size="lg" icon="fa-solid fa-chevron-left" />
               </span>
               <span>Choose car</span>
            </div>
            <div @click="isLoading || (selectedPaymentMethod === 'wallet' && !agencyWallet.has_sufficient_balance) ? null : handleBookingProceed()" :class="isLoading || (selectedPaymentMethod === 'wallet' && !agencyWallet.has_sufficient_balance) ? 'cursor-not-allowed opacity-60' : 'hover:bg-white hover:text-theme-active cursor-pointer'" class="gap-4 uppercase rounded-full px-8 py-4 border-2 border-theme-active bg-theme-active text-white text-size-2 flex items-center justify-center w-full md:w-auto md:ml-auto transition-all ease-in-out duration-500 order-1 md:order-2">
               <span v-if="isLoading">
                  Processing...
               </span>
               <span v-else>
                  {{ contactDetails.needConfirmation ? 'Request a Quote' : (selectedPaymentMethod === 'wallet' ? 'Pay by Agency Wallet' : 'Pay by Credit Card') }}
               </span>
               <span>
                  <font-awesome-icon v-if="isLoading" icon="fa-solid fa-spinner" spin />
                  <font-awesome-icon v-else size="lg" icon="fa-solid fa-chevron-right" />
               </span>
            </div>
         </div>
      </div>
   </div>
   <Notification :show="notif.show" :type="notif.type" :message="notif.message" :messageDetails="notif.messageDetails" @closed="notifClosed"/>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, onBeforeMount, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'
import BookingSummary from '@/components/BookingSummary.vue'
import CountrySearchablePhoneField from '@/components/CountrySearchablePhoneField.vue'
import { getPaymentSession, getSessionPrefix, handleApiError, isLoggedIn, isValidCardNumber, isValidEmail, resetErrors, savePaymentSession, savePaymentWallet, getBackendBaseUrl, getUserToken } from '@/utils/common'
import CarService from '@/services/car-service'
import { Car as CarData, UserSearch } from '@/types'
import { retrieveUserSearch } from '@/services/search-service'
import BookingService from '@/services/booking-service'
import Payment from '@/composables/Payment'
import { credimax } from '@/utils/constants'
import creditCardType from 'credit-card-type'
import axios from 'axios'
import Car from '@/composables/Car'
import { NotificationType } from '@/types'
import Notification from '@/widgets/Notification.vue'
import { useCoupon } from '@/composables/Coupon'
import sClassImg from '@/assets/cars/luxury-sedan.png'
import vClassImg from '@/assets/cars/van.png'
import eClassImg from '@/assets/cars/premium-sedan.png'
import gmcSuvImg from '@/assets/cars/suv.png'

declare global {
  interface Window {
    ThreeDS: any;
  }
}

const props = defineProps(['currentStep'])
const { retrieveUserSelectedCar, saveSelectedCar } = CarService()
const notif = ref<NotificationType>({ show: false, type: 'success', message: '', messageDetails: ''})
const { updateBooking, createBooking } = BookingService()
const { createToken, createSession, updateSession, initiateAuthentication, authenticatePayer: authenticatePayerService, authorizePayment } = Payment()
const authUser = isLoggedIn()
const isLogged = ref(!!authUser)

const router = useRouter()
const baseUrl = getBackendBaseUrl()

const agencyWallet = ref<any>({
   allow_wallet_booking: false,
   wallet_balance: 0.0,
   wallet_currency: 'USD',
   booking_amount: 0.0,
   has_sufficient_balance: false
})
const selectedPaymentMethod = ref<'card' | 'wallet'>('card')
const isWalletLoading = ref<boolean>(false)

const fetchAgencyWalletInfo = async (bookingNumber: string) => {
   if (!isLogged.value || authUser?.user?.type !== 'agent') return
   
   isWalletLoading.value = true
   try {
      const response = await axios.get(baseUrl + 'api/credit/agency-wallet/' + bookingNumber, {
         headers: {
            'Authorization': `Bearer ${getUserToken()}`,
            'Content-Type': 'application/json'
         }
      })
      if (response.status === 200 && response.data.success) {
         agencyWallet.value = response.data
      }
   } catch (error) {
      console.error('Failed to load agency wallet info:', error)
   } finally {
      isWalletLoading.value = false
   }
}

const contactDetails = ref<any>({
   firstName: '',
   phoneNumber: '',
   lastName: '',
   email: '',
   comments: '',
   passengers: null,
   bags: null,
   createAccount: false,
   needConfirmation: false
})
const paymentDetails = ref<any>({
   card: {
      name: '',
      number: '',
      cvc: '',
      expiration: ''
   },
   token: '',
   session: null,
   order: null,
   transaction: null
})
const errors = ref<any>({
   firstName: null,
   lastName: null,
   email: null,
   phoneNumber: null,
   cardName: null,
   cardNumber: null,
   cardCVC: null,
   cardExpiration: null,
   responseError: null
})
const isLoading = ref<boolean>(false)
const show3DS = ref<boolean>(false)

// Coupon Logic
const { applyCoupon: apiApply, validateCoupon } = useCoupon()
const showPromo = ref(false)
const promoCode = ref('')
const promoErrorMessage = ref('')
const isApplyingPromo = ref(false)
const appliedDiscount = ref<number | undefined>(undefined)
const newTotal = ref<number | undefined>(undefined)

const car = ref<CarData>(retrieveUserSelectedCar())
const carPrice = computed(() => car.value ? car.value.price : 0)
const userSearch = ref<UserSearch | null>(retrieveUserSearch())

const summaryKey = ref(0)
const isSwitchingCar = ref(false)
const switchingCarId = ref<number | null>(null)

const showUpgradeSuggestions = computed(() => {
   if (!car.value) return false
   const maxPax = car.value.type?.passengers || 0
   const maxBags = car.value.type?.bags || 0
   const enteredPax = parseInt(contactDetails.value.passengers) || 0
   const enteredBags = parseInt(contactDetails.value.bags) || 0
   
   return (enteredPax > 0 && enteredPax > maxPax) || (enteredBags > 0 && enteredBags > maxBags)
})

const getCarImage = (c: CarData) => {
   if (c.image && c.image !== '' && !c.image.includes('default.jpg')) {
      return c.image
   }
   const name = (c.type?.name || '').toLowerCase()
   if (name.includes('premium')) return eClassImg
   if (name.includes('luxury')) return sClassImg
   if (name.includes('van')) return vClassImg
   if (name.includes('suv')) return gmcSuvImg
   if (name.includes('bus')) return '/assets/busescoaches.jpeg'
   
   return '/assets/nocarfound.jpeg'
}

const suggestedCars = computed(() => {
   if (!showUpgradeSuggestions.value) return []
   const prefix = getSessionPrefix()
   const allSearched: CarData[] = JSON.parse(localStorage.getItem(`${prefix}_searched_cars`) || '[]')
   
   const enteredPax = parseInt(contactDetails.value.passengers) || 0
   const enteredBags = parseInt(contactDetails.value.bags) || 0
   
   return allSearched.filter(c => {
      // Exclude the current car
      if (c.id === car.value?.id) return false
      
      const paxCapacity = c.type?.passengers || 0
      const bagCapacity = c.type?.bags || 0
      
      // If they entered passenger count, target car must fit the passenger count
      if (enteredPax > 0 && paxCapacity < enteredPax) return false
      // If they entered bag count, target car must fit the bag count
      if (enteredBags > 0 && bagCapacity < enteredBags) return false
      
      return true
   })
})

const switchCar = async (newCar: CarData) => {
   isSwitchingCar.value = true
   switchingCarId.value = newCar.id
   try {
      const searchData = userSearch.value || retrieveUserSearch()
      const response = await createBooking(newCar, searchData as UserSearch)
      if (response.success) {
         saveSelectedCar(newCar)
         const prefix = getSessionPrefix()
         localStorage.setItem(`${prefix}_booking_number`, response.data.data.number)
         
         // Update local states
         car.value = newCar
         appliedDiscount.value = undefined
         newTotal.value = undefined
         promoCode.value = ''
         promoErrorMessage.value = ''
         summaryKey.value++
         
         notif.value = { 
            show: true, 
            type: 'success', 
            message: 'Vehicle Switched', 
            messageDetails: `Successfully upgraded to ${newCar.name} (${newCar.type?.name}).` 
         }
      } else {
         notif.value = { show: true, type: 'error', message: 'Switch Failed', messageDetails: typeof response.error === 'string' ? response.error : (response.error as any)?.message || 'Failed to switch vehicle.' }
      }
   } catch (err: any) {
      console.error(err)
      notif.value = { show: true, type: 'error', message: 'Error', messageDetails: 'An error occurred while switching vehicle.' }
   } finally {
      isSwitchingCar.value = false
      switchingCarId.value = null
   }
}

watch([showUpgradeSuggestions, suggestedCars, car], ([hasSuggestions, list, currentCar]) => {
   const isInitiallyOnRequest = Number(currentCar?.price || 0) <= 0
   if (isInitiallyOnRequest) {
      contactDetails.value.needConfirmation = true
   } else {
      contactDetails.value.needConfirmation = hasSuggestions && list.length === 0
   }
}, { immediate: true })

const togglePromo = () => {
    showPromo.value = !showPromo.value
    promoErrorMessage.value = ''
}

const applyPromoCode = async () => {
    isApplyingPromo.value = true
    const prefix = getSessionPrefix()
    const bookingNumber = localStorage.getItem(`${prefix}_booking_number`)

    if (!bookingNumber) {
        notif.value = { show: true, type: 'error', message: 'Error', messageDetails: 'Missing booking reference.' }
        isApplyingPromo.value = false
        return
    }

    if (!contactDetails.value.email) {
         notif.value = { show: true, type: 'warning', message: 'Email Required', messageDetails: 'Please enter your email address first to apply coupons.' }
         isApplyingPromo.value = false
         return
    }

    const { applyCoupon: apiApply, validateCoupon } = useCoupon()
    
    // First Validate against customer constraints
    const car: CarData = retrieveUserSelectedCar()
    const baseAmount = car ? car.price : 0
    
    const validRes = await validateCoupon(promoCode.value, contactDetails.value.email, baseAmount)
    if (validRes.status !== 'success') {
        promoErrorMessage.value = validRes.message
        isApplyingPromo.value = false
        return
    }

    // Now securely apply to DB
    const res = await apiApply(promoCode.value, bookingNumber)
    
    if (res.status === 'success') {
       promoErrorMessage.value = '' // ✅ Clear any old error message
       notif.value = { show: true, type: 'success', message: 'Offer Applied', messageDetails: res.data.message }
       appliedDiscount.value = res.data.discount_amount
       newTotal.value = res.data.new_total
    } else {
       notif.value = { show: true, type: 'error', message: 'Failed to apply coupon', messageDetails: res.message }
    }
    
    isApplyingPromo.value = false
}

// Expiry Dropdowns
const expiryMonth = ref('')
const expiryYear = ref('')

const updateExpiry = () => {
   if (expiryMonth.value && expiryYear.value) {
      paymentDetails.value.card.expiration = `${expiryYear.value}-${expiryMonth.value}`
   }
}

const handleBookingProceed = async () => {
      
      resetErrors(errors.value)
      if (!validateForm()) {
         notif.value = { show: true, type: 'error', message: 'Incomplete Details', messageDetails: 'Please fill in all required fields (marked in red) before proceeding.' }
         return
      }

      if (showUpgradeSuggestions.value && suggestedCars.value.length > 0) {
         notif.value = { 
            show: true, 
            type: 'error', 
            message: 'Capacity Exceeded', 
            messageDetails: 'Please upgrade to one of the suggested vehicles with sufficient passenger and luggage capacity above.' 
         }
         return
      }

      const prefix = getSessionPrefix()
      const bookingNumber = localStorage.getItem(`${prefix}_booking_number`)

      isLoading.value = true

      // ✅ 1. Sync backend
      const response = await updateBooking(bookingNumber as string, contactDetails.value, paymentDetails.value, contactDetails.value.needConfirmation)
      
      if (response.success) {
         if (contactDetails.value.needConfirmation) {
            isLoading.value = false
            router.push({name: 'BookingWizard', params: {step: 'finish'}})
         } else if (selectedPaymentMethod.value === 'wallet') {
            // WALLET DIRECT DEDUCTION PATH
            try {
               const payResponse = await axios.post(baseUrl + 'api/credit/agency-wallet/pay', {
                  booking: bookingNumber
               }, {
                  headers: {
                     'Authorization': `Bearer ${getUserToken()}`,
                     'Content-Type': 'application/json'
                  }
               })
               if (payResponse.status === 200 && payResponse.data.success) {
                  savePaymentWallet('wallet')
                  router.push({ 
                     name: 'BookingWizard', 
                     params: { step: 'finish' },
                     query: { status: 'success' }
                  })
               } else {
                  isLoading.value = false
                  errors.value.responseError = payResponse.data?.message || 'Failed to complete payment using wallet credits.'
               }
            } catch (err: any) {
               isLoading.value = false
               errors.value.responseError = err.response?.data?.message || 'Error occurred during wallet credit deduction.'
            }
         } else {
            // DIRECT PAYMENT PATH
            savePaymentWallet('card')
            paymentDetails.value.card.brand = getCardBrand(paymentDetails.value.card.number.toString())
            paymentDetails.value.order = bookingNumber

            const tokenResponse = await createToken(paymentDetails.value)
            if (tokenResponse.status == 201) {

               const sessionResponse = await updateSession(paymentDetails.value.session, tokenResponse.data.token, bookingNumber as string)
               
               if (sessionResponse.status == 200 && sessionResponse.data.success) {
                  paymentDetails.value.transaction = sessionResponse.data.transaction
                  initiate3DSAuthentication(paymentDetails.value.order, paymentDetails.value.transaction)
               } else {
                  isLoading.value = false
                  // Show real error from bank if available, otherwise fallback
                  const bankError = sessionResponse.data?.error?.explanation || sessionResponse.data?.error?.message
                  errors.value.responseError = bankError 
                    ? `Bank Error: ${bankError}` 
                    : 'Payment authorization setup failed. Please check your card details or try another card.'
                  
                  console.error('Session Update Failed:', sessionResponse.data)
               }

            } else {
               isLoading.value = false
               errors.value.responseError = 'Card validation failed. Please check your card number.'
            }
         }
      } else {
         isLoading.value = false
         const error = handleApiError(response)
         errors.value.responseError = error
      }
}

const validateForm = () => {
   
   let response = true
   
   if (contactDetails.value.firstName === '') {
      errors.value.firstName = true
      response = false
   }
   
   if (contactDetails.value.lastName === '') {
      errors.value.lastName = true
      response = false
   }
   
   if (contactDetails.value.phoneNumber === '') {
      errors.value.phoneNumber = 'Required'
      response = false
   }else if (contactDetails.value.phoneNumber.length < 11) {
      errors.value.phoneNumber = 'Invalid'
      response = false
   }

   if (contactDetails.value.email === '') {
      errors.value.email = 'Required'
      response = false
   } else if (!isValidEmail(contactDetails.value.email)) {
      errors.value.email = 'Invalid'
      response = false
   }
   
   if (!contactDetails.value.passengers) {
      errors.value.passengers = true
      response = false
   }

   if (contactDetails.value.bags === null || contactDetails.value.bags === undefined) {
      errors.value.bags = true
      response = false
   }
   
   if (contactDetails.value.needConfirmation === false && selectedPaymentMethod.value === 'card') {
      
      if (paymentDetails.value.card.name === '') {
         errors.value.cardName = true
         response = false
      }
   
      if (paymentDetails.value.card.number === '') {
         errors.value.cardNumber = 'Required'
         response = false
      } else if (!isValidCardNumber(paymentDetails.value.card.number)) {
         errors.value.cardNumber = 'Invalid'
         response = false
      }
   
      if (paymentDetails.value.card.cvc === '') {
         errors.value.cardCVC = true
         response = false
      }
   
      if (paymentDetails.value.card.expiration === '') {
         errors.value.cardExpiration = true
         response = false
      }
   }

   return response
}

const handlePhoneChange = (value: string) => {
   contactDetails.value.phoneNumber = value
}

const getPaymentToken = async () : Promise<any> => {

   let paymentToken

   /* Todo : generate a valid payment token from payment gateway */
   paymentToken = '123456789abcef'

   return { success: true, token: paymentToken }
}

const getCardBrand = (cardNumber: string) => {
   const cardType = creditCardType(cardNumber);

   if (cardType.length > 0) {
      return cardType[0].niceType
   }

   return 'Unknown'
}

onMounted(async () => {
   if (!car.value || !userSearch.value) {
      router.push('/')
      return
   }

   if (isLogged.value && authUser?.user && authUser.user.type === 'web') {
      contactDetails.value.firstName = authUser.user.firstName || ''
      contactDetails.value.lastName = authUser.user.lastName || ''
      contactDetails.value.email = authUser.user.email || ''
   }

   const response: any = retrieveUserSelectedCar()
   if (response && (response.price === null || response.price === 0 || response.price === 'on-request' || response.needConfirmation === true)) {
      contactDetails.value.needConfirmation = true
   }

   if (!contactDetails.value.needConfirmation) {
      const sessionResponse = await checkAndCreateSession()
      if (!sessionResponse.status) {
         notif.value = { show: true, type: 'error', message: 'Error', messageDetails: 'Payment cannot be processed at the moment'}
      } else {
         paymentDetails.value.session = sessionResponse.session
      }
      const prefix = getSessionPrefix()
      const bookingNumber = localStorage.getItem(`${prefix}_booking_number`)
      if (bookingNumber) {
         await fetchAgencyWalletInfo(bookingNumber)
      }
   }

   window.addEventListener('message', handlePaymentMessage)
})

onUnmounted(() => {
   window.removeEventListener('message', handlePaymentMessage)
})

const handlePaymentMessage = async (event: MessageEvent) => {
   if (event.data === 'redirect') {
      show3DS.value = false
      isLoading.value = true
      
      // Final Step: Authorize and Capture the payment
      console.log('Completing Payment with:', {
         session: paymentDetails.value.session,
         order: paymentDetails.value.order,
         transaction: paymentDetails.value.transaction,
         cvc: paymentDetails.value.card.cvc
      })
      const response = await authorizePayment(
         paymentDetails.value.session,
         paymentDetails.value.order,
         paymentDetails.value.transaction.toString(),
         paymentDetails.value.card.cvc.toString()
      )

      if (response.status === 200 && response.data.success) {
         // Redirect to success page with a success flag
         router.push({ 
            name: 'BookingWizard', 
            params: { step: 'finish' },
            query: { status: 'success' }
         })
      } else {
         isLoading.value = false
         notif.value = { 
            show: true, 
            type: 'error', 
            message: 'Payment Error', 
            messageDetails: response.data?.error?.explanation || response.data?.error || 'Payment authorization failed. Please contact support.' 
         }
      }
   } else if (event.data === 'error') {
      show3DS.value = false
      isLoading.value = false
      notif.value = { show: true, type: 'error', message: 'Payment Error', messageDetails: 'Authentication failed. Please try again.' }
   }
}

onBeforeMount(() => {
   const script = document.createElement('script')
   script.src = credimax.sdk
   script.dataset.error = 'errorCallback'
   script.dataset.cancel = 'cancelCallback'
   script.onload = async () => {
      const response = await configure3DS()
      if (!response) {
         notif.value = { show: true, type: 'error', message: 'Error', messageDetails: 'Payment partner configuration error'}
      }
   }
   document.head.appendChild(script)
})

const configure3DS = (): Promise<boolean> => {
   return new Promise((resolve, reject) => {
      window.ThreeDS.configure({
         merchantId: credimax.merchant_id,
         sessionId: paymentDetails.value.session,
         containerId: '3DSUI',
         callback: function () {
            resolve(window.ThreeDS.isConfigured())
         },
         configuration: {
            userLanguage: 'en-AU',
            wsVersion: 77,
         },
      })
   })
}

const initiate3DSAuthentication = async (order: string, transaction: string) => {

   if (!window.ThreeDS.isConfigured()) {
      await configure3DS()
   }

   const car : CarData = retrieveUserSelectedCar()
   const totalAmount = car ? car.price : 0

   const sessionResponse = await initiateAuthentication(paymentDetails.value.session, paymentDetails.value.order, paymentDetails.value.transaction)
   
   if (sessionResponse.status === 200) {
      const data = sessionResponse.data.response
      const recommendation = data.response ? data.response.gatewayRecommendation : data.gatewayRecommendation
      if (recommendation == 'PROCEED') {
         authenticatePayerLocal(order, transaction)
      } else {
         isLoading.value = false
         notif.value = { show: true, type: 'error', message: 'Error', messageDetails: 'Payment could not be initiated: ' + recommendation}
      }
   } else {
      isLoading.value = false
      notif.value = { show: true, type: 'error', message: 'Error', messageDetails: 'Server error during payment initiation'}
   }
}

const authenticatePayerLocal = async (order: string, transaction: string) => {

   isLoading.value = true
   const response = await authenticatePayerService(paymentDetails.value.session, order, transaction)
   if (response.status === 200) {
      const data = response.data.response
      if (data.authentication && data.authentication.redirect) {
         isLoading.value = false // Stop main loading before showing 3DS
         show3DS.value = true
         const html = data.authentication.redirect.html
         setTimeout(() => {
            const container = document.getElementById('3DSUI')
            if (container) {
               container.innerHTML = html
               const form = container.querySelector('form')
               if (form) form.submit()
            }
         }, 100)
      } else {
         isLoading.value = false
      }
   } else {
      isLoading.value = false
      notif.value = { show: true, type: 'error', message: 'Error', messageDetails: 'Failed to connect to bank. Please try again.'}
   }
}


const checkAndCreateSession = async () : Promise<{status: boolean, session: string | null}> => {

   const sessionResponse = await createSession()
   if (sessionResponse.status == 200) {
      savePaymentSession(sessionResponse.data.session)
      return {status: true, session: sessionResponse.data.session}
   } else {
      return {status: false, session: null}
   }
}

const notifClosed = () : void => {
   notif.value.show = false
}

</script>